import { InboxOutlined } from '@ant-design/icons';
import { CustomTableColumn, UserCustomAttributeType } from '@hefe/models';
import { UsersImporterSlice } from '@hefe/store';
import {
  Alert,
  Button,
  Radio,
  Row,
  Space,
  Table,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { t } from '@hefe/i18n';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';

interface UsersImporterError {
  row: number;
  column: number | null;
  text: string;
}

export default function UsersImporterDataImporterComponent() {
  const dispatch = useAppDispatch();
  const headers = useSelector(UsersImporterSlice.usersImporterHeadersSelector);

  const [infoText, setInfoText] = useState<string>('');
  const [errors, setErrors] = useState<UsersImporterError[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [dataWithoutErrors, setDataWithoutErrors] = useState<any[]>([]);
  const [separatorType, setSeparatorType] = useState('tab');

  const attributeTypes = [
    {
      value: UserCustomAttributeType.STRING,
      text: t('text'),
      pattern: new RegExp(''),
      error: '',
    },
    {
      value: UserCustomAttributeType.NUMBER,
      text: t('number'),
      pattern: new RegExp('^-?\\d*(\\.\\d+)?$'),
      error: 'Bitte Zahl eingeben',
    },
    {
      value: UserCustomAttributeType.DATE,
      text: t('date'),
      pattern: new RegExp(
        '^\\s*(3[01]|[12][0-9]|0?[1-9])\\.(1[012]|0?[1-9])\\.(\\d{4})\\s*$'
      ),
      error: 'Bitte Datum im Format DD.MM.JJJJ eingeben',
    },
    {
      value: UserCustomAttributeType.MAIL,
      text: t('email'),
      pattern: new RegExp(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      ),
      error: 'Bitte E-Mail Adresse eingeben',
    },
    {
      value: UserCustomAttributeType.SEX,
      text: t('sex.title'),
      pattern: new RegExp(''),
      error: 'Bitte Geschlecht angeben (m/w/d)',
    },
    {
      value: UserCustomAttributeType.MULTI_MAIL,
      text: t('email'),
      pattern: new RegExp(
        /^[\W]*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4}[\W]*,{1}[\W]*)*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4})[\W]*$/
      ),
      error: 'Bitte E-Mail Adresse eingeben',
    },
  ];

  /********************************************************************************************************************************
   *
   *                                                        Functions
   *
   *******************************************************************************************************************************/

  useEffect(() => {
    document.addEventListener('paste', (event: ClipboardEvent) => {
      if (!event.clipboardData) {
        return;
      }

      const clipboardData = event.clipboardData.getData('Text');
      processPaste(clipboardData);
    });
  }, []);

  const onNext = () => {
    dispatch(
      UsersImporterSlice.usersImporterActions.setData(dataWithoutErrors)
    );
    dispatch(UsersImporterSlice.usersImporterActions.incrementStep(4));
  };

  const getColumns = () => {
    const returnValue: CustomTableColumn[] = [];

    headers.forEach((header, columnIndex) => {
      returnValue.push({
        key: header.key,
        title: header.title,
        dataIndex: header.key,
        default: false,
        render: (text: any, record: any) => {
          const value = record[columnIndex];
          const rowIndex = data.findIndex((r) => r === record);

          const error = errors.find(
            (err: any) => err.row === rowIndex && err.column === columnIndex
          );

          if (error) {
            return {
              props: {
                style: { background: '#ffcccb' },
              },
              children: <Tooltip title={error.text}>{value}</Tooltip>,
            };
          } else {
            return value;
          }
        },
      });
    });

    return returnValue;
  };

  /********************************************************************************************************************************
   *
   *                                                        IMPORT DATA LOGIC
   *
   *******************************************************************************************************************************/

  const processPaste = (data: string) => {
    const output: any[] = [];
    const outputWithoutErrors: any[] = [];
    let validRows = 0;

    setInfoText('');
    setErrors([]);

    const rows = data.split('\n');
    const separator = separatorType === 'tab' ? '\t' : ';';

    rows.forEach((row, rowIndex) => {
      const rowArray = row.split(separator);
      if (rowArray.length === headers.length) {
        if (validateRow(rowArray, rowIndex)) {
          validRows += 1;
          outputWithoutErrors.push(rowArray);
        }
        output.push(rowArray);
      } else {
        addError(
          rowIndex,
          null,
          t('users_importer.columns_not_matching', {
            expected: headers.length,
            received: rowArray.length,
          })
        );
      }
    });

    setInfoText(
      t('users_importer.num_of_valid_rows', {
        total: rows.length,
        valid: validRows,
      })
    );
    setData(output);
    setDataWithoutErrors(outputWithoutErrors);
  };

  const validateRow = (row: any[], rowIndex: number) => {
    let rowValid = true;

    row.forEach((field, index) => {
      const fieldValid = validateField(
        field,
        headers[index].type,
        index,
        rowIndex
      );
      rowValid = rowValid && fieldValid;
    });

    return rowValid;
  };

  const validateField = (
    field: any,
    headerFieldType: UserCustomAttributeType,
    columnIndex: number,
    rowIndex: number
  ) => {
    const type = attributeTypes.find((type) => type.value === headerFieldType);
    const column = headers[columnIndex];

    const trimmedField = field.replace(/[\r\n]/gm, '').trim();

    if (column.nullable && (!trimmedField || trimmedField.length === 0)) {
      return true;
    }

    if (
      ![UserCustomAttributeType.STRING, UserCustomAttributeType.SEX].includes(
        headerFieldType
      )
    ) {
      if (type && !type.pattern.test(trimmedField)) {
        addError(rowIndex, columnIndex, `${t('error')}! ${type.error}`);
        return false;
      }
    } else if (headerFieldType === UserCustomAttributeType.SEX) {
      return ['m', 'w', 'd'].includes(trimmedField);
    }

    return true;
  };

  const addError = (row: number, column: number | null, text: string) => {
    const newError = {
      row: row,
      column: column,
      text: text,
    };

    setErrors((currentErrors) => [...currentErrors, newError]);
  };

  const renderErrorAlert = () => {
    const errorTexts: any[] = [];
    errors.forEach((error) => {
      errorTexts.push(
        <Typography.Text key={error.row}>
          {t('row')}: {error.row + 1}: {error.text}
        </Typography.Text>
      );
    });
    return (
      <Alert
        type="error"
        message={
          <Space direction="vertical" size={0}>
            {errorTexts}
          </Space>
        }
      />
    );
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space>
        <Typography.Text>{t('users_importer.csv_separator')}:</Typography.Text>
        <Radio.Group
          value={separatorType}
          defaultValue={'tab'}
          onChange={(event) => setSeparatorType(event.target.value)}
        >
          <Radio.Button value="tab">{t('tab')}</Radio.Button>
          <Radio.Button value="comma">{t('semicolon')}</Radio.Button>
        </Radio.Group>
      </Space>
      {infoText !== '' && <Alert message={infoText} type="info" />}
      {errors.length > 0 && renderErrorAlert()}
      <Upload.Dragger
        {...{
          accept: '.csv, .txt',
          showUploadList: false,
          beforeUpload: (file) => {
            const reader = new FileReader();

            reader.onload = (e: ProgressEvent<FileReader>) => {
              const result = e.target?.result as string;
              if (result) {
                processPaste(result);
              }
            };
            reader.readAsText(file);
            return false;
          },
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{t('users_importer.drop_csv_here')}</p>
        <p className="ant-upload-hint">
          {t('users_importer.alternative_ctrl_v')}
          <br />
          {t('users_importer.columns_must_match')}
        </p>
      </Upload.Dragger>
      <Table
        columns={getColumns()}
        dataSource={data}
        scroll={{
          x: true,
        }}
      />
      <Row justify="end">
        <Space>
          <Button
            onClick={() =>
              dispatch(UsersImporterSlice.usersImporterActions.decrementStep())
            }
          >
            {t('prev')}
          </Button>
          <Button disabled={data.length === 0} onClick={() => onNext()}>
            {t('users_importer.continue_with', {
              number: dataWithoutErrors.length,
            })}
          </Button>
        </Space>
      </Row>
    </Space>
  );
}
